/**
 * Google Analytics 4 (gtag) – nur nach Einwilligung (DSGVO-konformer Ansatz).
 * Kein Laden von googletagmanager.com vor Zustimmung.
 */

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: Function;
  }
}

const STORAGE_KEY = 'portfolio-wizard-analytics-consent';
/** Bei Änderung der Datenschutzhinweise erhöhen → Banner erneut anzeigen */
const CONSENT_VERSION = '1';

export const GA_MEASUREMENT_ID =
  (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined) || 'G-JT8TLL2DKD';

export type AnalyticsConsentChoice = 'granted' | 'denied';

interface StoredConsent {
  v: string;
  choice: AnalyticsConsentChoice;
}

export function getStoredConsent(): AnalyticsConsentChoice | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    if (parsed?.v !== CONSENT_VERSION) return null;
    if (parsed.choice === 'granted' || parsed.choice === 'denied') return parsed.choice;
  } catch {
    // ignore
  }
  return null;
}

export function setStoredConsent(choice: AnalyticsConsentChoice): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ v: CONSENT_VERSION, choice }));
  } catch {
    // ignore
  }
}

let loadPromise: Promise<void> | null = null;
/** true, sobald gtag.js geladen und config einmal gesetzt wurde (über Lebensdauer der SPA). */
let gtagLibraryReady = false;
/** Nach Widerruf: keine Pageviews mehr (gtag-Consent allein reicht ohne vorheriges Consent-Default nicht zuverlässig). */
let analyticsRevoked = false;

const gaDebugMode = import.meta.env.VITE_GA_DEBUG === 'true';

/** Consent Mode v2: Messung nur nach Opt-in; ohne explizites „granted“ können Hits in der EU ausbleiben. */
function grantAnalyticsStorageInTag(): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
}

/** Lädt gtag.js und initialisiert GA4 (idempotent). Nach Widerruf + erneutem Akzeptieren: nur Consent-Update, kein zweites Laden. */
export function loadGtag(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  analyticsRevoked = false;

  if (gtagLibraryReady && document.querySelector('script[data-portfolio-wizard-gtag="1"]')) {
    return Promise.resolve().then(() => {
      grantAnalyticsStorageInTag();
    });
  }

  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    window.dataLayer = window.dataLayer || [];
    // WICHTIG: Google's gtag.js erwartet Arguments-Objekte im dataLayer-Backlog,
    // KEINE Arrays. Rest-Parameter (...args) erzeugen Arrays, die gtag.js ignoriert.
    // Daher hier bewusst `arguments` statt Rest-Parameter verwenden.
    window.gtag = function () {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };

    // Consent Mode v2: Default MUSS vor dem Laden von gtag.js gesetzt werden,
    // sonst verwirft GA4 in der EU alle Hits (wait_for_update gibt GA Zeit für das spätere Update).
    window.gtag('consent', 'default', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      wait_for_update: 500,
    });

    const script = document.createElement('script');
    script.async = true;
    script.dataset.portfolioWizardGtag = '1';
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID)}`;
    script.onload = () => {
      window.gtag?.('js', new Date());
      grantAnalyticsStorageInTag();
      window.gtag?.('config', GA_MEASUREMENT_ID, {
        send_page_view: false,
        ...(gaDebugMode ? { debug_mode: true } : {}),
      });
      gtagLibraryReady = true;
      resolve();
    };
    script.onerror = () => {
      loadPromise = null;
      reject(new Error('gtag script failed to load'));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}

export function isGtagAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
}

/** Nach Widerruf: keine weiteren Messungen melden (wenn gtag bereits geladen war). */
export function revokeAnalyticsStorage(): void {
  analyticsRevoked = true;
  if (!isGtagAvailable()) return;
  window.gtag!('consent', 'update', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
}

export function trackPageView(pagePath: string): void {
  if (analyticsRevoked || !isGtagAvailable()) return;
  const path = pagePath.startsWith('/') ? pagePath : `/${pagePath}`;
  window.gtag!('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}
